import React, { useEffect, useRef, useState } from 'react';
import { Col, Row } from 'react-bootstrap';
import { useTranslation } from 'react-i18next';
import InfiniteScroll from 'react-infinite-scroll-component';
import { useSelector } from 'react-redux';
import { useLocation, useParams } from 'react-router-dom';
import { InfoBook } from '../../../../../model/book/Book';
import { IParams } from '../../../../../model/shared/IParams';
import EmptyState from '../../../../../shared/components/placeholder/EmptyState';
import { isDateInPast } from '../../../../../shared/functions/Functions';
import { IAllStates } from '../../../../../store/rootReducer';
import { gtmService } from '../../../../../utils/gtm.service';
import { ErrorToast, SuccessToast, WarningToast } from '../../../../../utils/toasters';
import { IDeclineOffer } from '../../../../influencer/offer/dto/IDeclineOffer';
import { ContractService } from '../service';
import { BookingContractsCardItem } from './BookingContractsCardItem';
import { BookingContractsFilter } from './BookingContractsFilter';
import { BookingContractsListItem } from './BookingContractsListItem';
import BookingOverviewContractsCard from './BookingOverviewContractsCard';
import { IContractFilter } from './dto/IContractFilter';
import { IContractOverviewCart } from './dto/IContractOverviewCart';
import { IContract } from '../../../../../model/shared/IContract';
import { Platforms } from '../../../../../model/platform/Platforms';
import { StoreKeeper } from '../../../../../store/store';
import { loadingSlice } from '../../../../../store/general/loadingSlice';
import { BookingCardSkeleton } from '../../book/offer/BookingCardSkeleton';
import { BookingOverviewCardSkeleton } from '../../book/offer/BookingOverviewCardSkeleton';

interface ILocationState {
    isSentActive: boolean;
    isFromNotification?: boolean;
}

const SKELETON_COUNT = 8;

export function ContractsList() {
    StoreKeeper.store.dispatch(loadingSlice.actions.setIsLoading(false));
    const location = useLocation();
    const { campaign } = useSelector((states: IAllStates) => states.selectedCampaign);
    const { isClient } = useSelector((states: IAllStates) => states.auth.user);
    const [infoCampaign, setInfoCampaign] = useState<InfoBook>();
    const [isContractsLoading, setIsContractsLoading] = useState(true);
    const [isOverviewLoading, setIsOverviewLoading] = useState(true);
    const lastQueryKeyRef = useRef<string>();
    const [filterParams, setFilterParams] = useState<IContractFilter>({
        page: 1,
        perPage: 10,
        sortCriteria: '',
        sortType: 'DESC',
        platformId: [],
        isBestPick: false,
        reachFrom: '',
        reachTo: '',
        priceRangeFrom: '',
        priceRangeTo: '',
        influencerLocation: undefined,
        audienceLocation: undefined,
        offerFilters: (location?.state as ILocationState)?.isSentActive
            ? 'sent'
            : getActiveContractsFilter(infoCampaign),
        status: undefined,
        displayName: '',
    });

    useEffect(() => {
        if (isClient) {
            setFilterParams(prev =>
                prev.offerFilters === 'proposed' ? prev : { ...prev, offerFilters: 'proposed', page: 1, perPage: 10 }
            );
        }
    }, [isClient]);

    const [activeCard, setActiveCard] = useState(true);
    const [contracts, setContracts] = useState<IContract[]>([]);
    const [socialPlatforms, setSocialPlatforms] = useState<Platforms[]>([]);
    const [overviewCart, setOverviewCart] = useState<IContractOverviewCart>();
    const [contractCount, setContractCount] = useState(0);
    const [updateContracts, setUpdateContracts] = useState(false);
    const [declineReasons, setDeclineReasons] = useState<IDeclineOffer>({ declineReason: '', declineComment: '' });
    const { hasUnreadComments } = useSelector((state: IAllStates) => state.contractBrand);
    const { id } = useParams<keyof IParams>();
    const { t } = useTranslation();

    useEffect(() => {
        if (
            +filterParams.priceRangeFrom &&
            +filterParams?.priceRangeTo &&
            +filterParams.priceRangeFrom > +filterParams?.priceRangeTo
        ) {
            WarningToast(t('general.toasts.invalidRange'));
            setIsContractsLoading(false);
            return;
        }
        if (!id) {
            return;
        }

        const queryKey = `${id}-${JSON.stringify({ ...filterParams, perPage: undefined })}`;
        if (lastQueryKeyRef.current !== queryKey) {
            lastQueryKeyRef.current = queryKey;
            setIsContractsLoading(true);
        }

        let isCurrent = true;
        ContractService.getContracts(+id, filterParams)
            .then(response => {
                if (!isCurrent) return;
                setContracts(response?.data?.contracts);
                setInfoCampaign(response?.data?.info);
                setContractCount(response.count);
            })
            .catch(error => ErrorToast(error))
            .finally(() => {
                if (isCurrent) setIsContractsLoading(false);
            });

        return () => {
            isCurrent = false;
        };
    }, [JSON.stringify(filterParams), updateContracts, id, hasUnreadComments]);

    useEffect(() => {
        if (!id) return;
        ContractService.getContractCartInfo(+id)
            .then(response => {
                setOverviewCart(response);
            })
            .catch(err => ErrorToast(err))
            .finally(() => setIsOverviewLoading(false));
    }, []);

    useEffect(() => {
        ContractService.getPlatform({ isEnabled: true })
            .then(response => {
                setSocialPlatforms(response.data);
            })
            .catch(err => ErrorToast(err));
    }, []);

    function acceptOffer(offerId: number) {
        ContractService.acceptContract(offerId)
            .then(response => {
                gtmService('brand_offer_accepted');
                SuccessToast(t('brand.campaign.book.offer.accept_offer'));
                setUpdateContracts(prevState => !prevState);
                setOverviewCart(response);
            })
            .catch(error => ErrorToast(error));
    }

    function declineContract(offerId: number) {
        ContractService.declineContract(offerId, declineReasons)
            .then(() => {
                SuccessToast(t('brand.campaign.book.offer.decline_offer'));
                setUpdateContracts(prevState => !prevState);
            })
            .catch(error => ErrorToast(error));
    }

    function approveProposal(contractId: number) {
        ContractService.approveProposal(contractId)
            .then(() => {
                SuccessToast(t('brand.campaign.book.offer.approve_proposal_success'));
                setUpdateContracts(prevState => !prevState);
            })
            .catch(error => ErrorToast(error));
    }

    function declineProposal(contractId: number) {
        ContractService.declineProposal(contractId, declineReasons)
            .then(() => {
                SuccessToast(t('brand.campaign.book.offer.decline_proposal_success'));
                setUpdateContracts(prevState => !prevState);
            })
            .catch(error => ErrorToast(error));
    }

    function deleteProposal(contractId: number) {
        ContractService.deleteProposal(contractId)
            .then(() => {
                SuccessToast(t('brand.campaign.book.offer.delete_proposal_success'));
                setUpdateContracts(prevState => !prevState);
            })
            .catch(error => ErrorToast(error));
    }

    function selectContract(contractId: number, isSelected: boolean) {
        ContractService.selectContract(contractId)
            .then(response => {
                const allContracts = [...contracts];
                const selectedOfferItem = allContracts.find(item => item.id === contractId);
                if (selectedOfferItem) {
                    selectedOfferItem.isSelected = isSelected;
                    const index = selectedOfferItem ? allContracts.indexOf(selectedOfferItem) : 0;
                    allContracts[index] = selectedOfferItem;
                }
                setContracts(allContracts);
                setOverviewCart(response);
            })
            .catch(error => ErrorToast(error));
    }

    function setBestPickAction(offerId: number) {
        ContractService.setBestPick(offerId)
            .then(() => {
                setUpdateContracts(prevState => !prevState);
            })
            .catch(error => ErrorToast(error));
    }

    const clearSelectedContract = () => {
        ContractService.clearSelectedContracts(id)
            .then(response => {
                setUpdateContracts(prevState => !prevState);
                setOverviewCart(response);
            })
            .catch(error => ErrorToast(error));
    };

    function getActiveContractsFilter(info: InfoBook | undefined) {
        if (isClient) {
            return 'proposed';
        }
        if (isDateInPast(campaign?.publishFrom ?? '')) {
            return 'booked';
        }
        if (!info?.bookingCount) {
            return 'received';
        }
        return 'booked';
    }

    const loadMoreData = () => {
        setFilterParams(prev => ({ ...prev, perPage: prev.perPage + 20 }));
    };

    return (
        <Row>
            <Col xl={isClient ? 12 : 9}>
                <BookingContractsFilter
                    activeCard={activeCard}
                    setActiveCard={setActiveCard}
                    info={infoCampaign}
                    contractCount={contractCount}
                    filterParams={filterParams}
                    setFilterParams={setFilterParams}
                    clearSelectedContract={clearSelectedContract}
                    socialPlatforms={socialPlatforms}
                    isFilterLoading={isContractsLoading}
                />
                {isContractsLoading ? (
                    <Row>
                        {Array.from({ length: SKELETON_COUNT }).map((_, i) => (
                            <BookingCardSkeleton key={i} />
                        ))}
                    </Row>
                ) : (
                    <InfiniteScroll
                        dataLength={contracts?.length}
                        next={loadMoreData}
                        endMessage={
                            <p
                                className={`text-center ${
                                    contracts?.length !== contractCount || filterParams?.perPage <= 10 ? 'd-none' : ''
                                }`}
                            >
                                <b>You have seen all!</b>
                            </p>
                        }
                        hasMore={contracts?.length < contractCount}
                        loader={
                            <Row>
                                <BookingCardSkeleton />
                                <BookingCardSkeleton />
                            </Row>
                        }
                    >
                        <div className={!!contracts?.length ? '' : 'd-none'}>
                            {activeCard ? (
                                <Row>
                                    <BookingContractsCardItem
                                        setDeclineReason={setDeclineReasons}
                                        contracts={contracts}
                                        setContracts={setContracts}
                                        acceptContract={acceptOffer}
                                        declineContract={declineContract}
                                        approveProposal={approveProposal}
                                        declineProposal={declineProposal}
                                        deleteProposal={deleteProposal}
                                        setBestPickAction={setBestPickAction}
                                        selectContract={selectContract}
                                        setUpdateContracts={setUpdateContracts}
                                    />
                                </Row>
                            ) : (
                                <BookingContractsListItem
                                    setDeclineReason={setDeclineReasons}
                                    contracts={contracts}
                                    setContracts={setContracts}
                                    acceptContract={acceptOffer}
                                    declineContract={declineContract}
                                    approveProposal={approveProposal}
                                    declineProposal={declineProposal}
                                    deleteProposal={deleteProposal}
                                    setBestPickAction={setBestPickAction}
                                    selectContract={selectContract}
                                    setUpdateContracts={setUpdateContracts}
                                />
                            )}
                        </div>
                        {!contracts?.length && (
                            <EmptyState
                                desc={t(
                                    isClient
                                        ? 'brand.campaign.book.contract.empty_contracts_client'
                                        : 'brand.campaign.book.contract.empty_contracts'
                                )}
                                iconPath="/icons/emptyBrandCampaignBook.png"
                                btnText={isClient ? undefined : `Influencer ${t('general.button.search')}`}
                                navigateTo={isClient ? undefined : `/brand/campaign/contracts/${id}/invite`}
                                addClass="col-lg-5 col-xl-7 mb-4 col-8 text-center mx-auto"
                                disableButton={
                                    infoCampaign?.campaign
                                        ? infoCampaign?.campaign?.approvalStatus === 'pending' ||
                                          isDateInPast(infoCampaign?.campaign?.publishTo ?? '')
                                        : true
                                }
                                tooltipDesc={
                                    infoCampaign?.campaign?.approvalStatus === 'pending'
                                        ? t(`brand.campaign.book.invite.filters.not_approved_campaign`)
                                        : t(`brand.campaign.book.invite.filters.publication_period_passed`)
                                }
                            />
                        )}
                    </InfiniteScroll>
                )}
            </Col>
            {!isClient && (
                <Col xl={3}>
                    {isOverviewLoading ? (
                        <BookingOverviewCardSkeleton />
                    ) : (
                        <BookingOverviewContractsCard
                            contractsFilter={filterParams.offerFilters ?? ''}
                            overviewCartInfo={overviewCart}
                            hasPdf={!!contracts.length}
                        />
                    )}
                </Col>
            )}
        </Row>
    );
}
